import "../styles/IndicatorsStyle.css";

import { useEffect, useState } from "react";

import { base_url, get_data } from "../Geoportal";
import { get_info_element } from "./Indicators";
import { customoptions, get_select_element } from "./Utils";

// dataset_id -> the exact CombinedProductAsset key the pipeline reports to
// (see adma_data_pipeline forecast_combine/job.py HAZARD_DATASET_IDS).
const DATASET_IDS = {
  era5: "combined_drought_hazard_era5",
  gpcc: "combined_drought_hazard_gpcc",
};

// Same 9-class palette as styles/hazard.py (pipeline) / LEGEND_COLORS["combined_hazard"]
// (backend) -- kept in sync manually, there's no live legend endpoint for a
// TiTiler-served COG the way GeoServer's GetLegendGraphic covers WMS layers.
export const COMBINED_HAZARD_LEGEND = [
  { color: "#f5f5f5", label: "No drought hazard" },
  { color: "#fbfa86", label: "Very low" },
  { color: "#ffff01", label: "Low (Watch)" },
  { color: "#ffd001", label: "Low-moderate" },
  { color: "#ffa601", label: "Moderate (Warning)" },
  { color: "#ff5301", label: "Moderate-high" },
  { color: "#ff0000", label: "High (Alert)" },
  { color: "#d20a0b", label: "Very high" },
  { color: "#a50f15", label: "Extreme" },
];

// Pulls one combined-hazard dataset's catalog entry -- visualization block
// for either the latest composite (year/period omitted) or a specific
// historical one -- plus available_periods, the {year, period} pairs the
// pipeline has ever reported (forecast_combine's historical backfill, see
// adma_data_pipeline/job.py run_forecast_combine period/year kwargs).
const fetch_combined_hazard_entry = async (source, year, period) => {
  let url = `${base_url}/api/data_api/rs_data/get_dataset_catalog?key=${DATASET_IDS[source]}`;
  if (year && period) {
    url += `&year=${year}&period=${encodeURIComponent(period)}`;
  }
  const catalog = await get_data(url);
  const entry = (catalog?.datasets || []).find((d) => d.key === DATASET_IDS[source]);
  if (!entry) {
    console.warn(`combined hazard: ${DATASET_IDS[source]} not found in get_dataset_catalog`);
  } else if (!entry?.visualization?.tile_url) {
    console.warn(`combined hazard: ${DATASET_IDS[source]} has no visualization block`, entry);
  }
  return entry;
};

// available_periods ([{year, period}, ...], newest first) -> {years, periodsByYear}
// so the dropdowns can offer only the (year, period) combinations the
// pipeline has actually produced, same constraint idea as Drought
// Persistence's getAllowedMonths/DekadsDroughtPersistence.
const index_periods = (available_periods) => {
  const periods_by_year = {};
  (available_periods || []).forEach(({ year, period }) => {
    if (!periods_by_year[year]) periods_by_year[year] = [];
    if (!periods_by_year[year].includes(period)) periods_by_year[year].push(period);
  });
  const years = Object.keys(periods_by_year)
    .map(Number)
    .sort((a, b) => b - a);
  return { years, periods_by_year };
};

const CombinedHazard = ({
  source, // "era5" | "gpcc"
  label,
  combined_hazard_check,
  setCombined_hazard_check,
  setCurrent_combined_hazard,
}) => {
  const [selected_dates, setSelected_dates] = useState({ year: null, period: null });
  const [available_years, setAvailable_years] = useState([]);
  const [periods_by_year, setPeriods_by_year] = useState({});

  const oncheck_combined_hazard = () => {
    setCombined_hazard_check(!combined_hazard_check);
  };

  const apply_entry = (entry) => {
    const { years, periods_by_year: by_year } = index_periods(entry?.available_periods);
    setAvailable_years(years);
    setPeriods_by_year(by_year);
    const slice = entry?.visualization?.slice;
    if (slice?.year && slice?.period) {
      setSelected_dates({ year: slice.year, period: slice.period });
    }
    setCurrent_combined_hazard(entry?.visualization?.tile_url || null);
  };

  useEffect(() => {
    let cancelled = false;
    if (combined_hazard_check) {
      // Reuse whatever period is already selected (so toggling off/on keeps
      // the user's choice); first-ever check has no selection yet, so
      // year/period are both null and the fetch falls through to "latest".
      fetch_combined_hazard_entry(source, selected_dates.year, selected_dates.period)
        .then((entry) => {
          if (!cancelled) apply_entry(entry);
        })
        .catch((error) => {
          console.error(`Failed to fetch combined hazard (${source}) tile_url:`, error);
          if (!cancelled) setCurrent_combined_hazard(null);
        });
    } else {
      setCurrent_combined_hazard(null);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combined_hazard_check]);

  const refetch_for = (year, period) => {
    fetch_combined_hazard_entry(source, year, period)
      .then((entry) => setCurrent_combined_hazard(entry?.visualization?.tile_url || null))
      .catch((error) => {
        console.error(`Failed to fetch combined hazard (${source}) for ${year}/${period}:`, error);
        setCurrent_combined_hazard(null);
      });
  };

  const onchange_year = (option) => {
    const year = option.value;
    const periods = periods_by_year[year] || [];
    const period = periods.includes(selected_dates.period) ? selected_dates.period : periods[0];
    setSelected_dates({ year, period });
    refetch_for(year, period);
  };

  const onchange_period = (option) => {
    const period = option.value;
    setSelected_dates((prev) => ({ ...prev, period }));
    refetch_for(selected_dates.year, period);
  };

  const period_options = periods_by_year[selected_dates.year] || [];

  return (
    <div className="selection-section">
      <div className="selection-section-header">
        <label className="switch">
          <input
            type="checkbox"
            defaultChecked={combined_hazard_check}
            onChange={oncheck_combined_hazard}
            disabled={false}
          />
          <span className="slider round"></span>
        </label>
        <p className="indicator-description combined-hazard-description">{label}</p>
        {get_info_element()}
      </div>

      {combined_hazard_check && selected_dates.year && (
        <p
          style={{
            position: "relative",
            color: "#000000",
            fontSize: "10px",
            left: "5px",
            marginTop: "10px",
          }}
        >
          {selected_dates.year} {selected_dates.period}
        </p>
      )}

      <div className="selection-row">
        {get_select_element(
          customoptions,
          available_years,
          onchange_year,
          selected_dates.year,
          combined_hazard_check
        )}
        {get_select_element(
          customoptions,
          period_options,
          onchange_period,
          selected_dates.period,
          combined_hazard_check
        )}
      </div>
    </div>
  );
};

export default CombinedHazard;
